import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  SelectAll,
  Clear,
  PlayArrow,
  Search,
  CheckCircle,
  Language
} from '@mui/icons-material';

const PageSelector = ({ siteMap, onStartScraping, initialSelectedPages = [] }) => {
  const [selectedPages, setSelectedPages] = useState(new Set(initialSelectedPages));
  const [searchTerm, setSearchTerm] = useState('');
  const [depthFilter, setDepthFilter] = useState('all');
  const [filteredPages, setFilteredPages] = useState([]);

  // Update selected pages when props change (for back navigation)
  useEffect(() => {
    setSelectedPages(new Set(initialSelectedPages));
  }, [initialSelectedPages]);

  useEffect(() => {
    if (!siteMap || !siteMap.all_pages) return;

    let pages = siteMap.all_pages.filter(page => page.accessible);

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      pages = pages.filter(page => 
        page.url.toLowerCase().includes(term) ||
        (page.title && page.title.toLowerCase().includes(term))
      );
    }

    // Apply depth filter
    if (depthFilter !== 'all') {
      pages = pages.filter(page => page.depth === parseInt(depthFilter));
    }

    setFilteredPages(pages);
  }, [siteMap, searchTerm, depthFilter]);

  const handleSelectAll = () => {
    const allPageUrls = new Set(filteredPages.map(page => page.url));
    setSelectedPages(allPageUrls);
  };

  const handleDeselectAll = () => {
    setSelectedPages(new Set());
  };

  const handlePageToggle = (url) => {
    const newSelected = new Set(selectedPages);
    if (newSelected.has(url)) {
      newSelected.delete(url);
    } else {
      newSelected.add(url);
    }
    setSelectedPages(newSelected);
  };

  const handleStartScraping = () => {
    const selectedUrls = Array.from(selectedPages);
    onStartScraping(selectedUrls);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getTotalSize = () => {
    return filteredPages
      .filter(page => selectedPages.has(page.url))
      .reduce((total, page) => total + (page.size || 0), 0);
  };

  if (!siteMap || !siteMap.all_pages) {
    return null;
  }

  const accessiblePages = siteMap.all_pages.filter(page => page.accessible);
  const maxDepth = Math.max(...accessiblePages.map(page => page.depth));

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Select Pages to Scrape
      </Typography>

      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search pages"
          placeholder="Search by URL or title..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ minWidth: 250 }}
        />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Depth</InputLabel>
          <Select
            value={depthFilter}
            label="Depth"
            onChange={(e) => setDepthFilter(e.target.value)}
          >
            <MenuItem value="all">All Depths</MenuItem>
            {Array.from({ length: maxDepth + 1 }, (_, i) => (
              <MenuItem key={i} value={i.toString()}>Depth {i}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<SelectAll />}
          onClick={handleSelectAll}
          size="small"
        >
          Select All
        </Button>

        <Button
          variant="outlined"
          startIcon={<Clear />}
          onClick={handleDeselectAll}
          size="small"
        >
          Deselect All
        </Button>
      </Box>

      {/* Selection Summary */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Chip 
          icon={<CheckCircle />}
          label={`${selectedPages.size} Selected`} 
          color={selectedPages.size > 0 ? 'primary' : 'default'}
          variant="outlined"
        />
        <Chip 
          icon={<Language />}
          label={`${filteredPages.length} Available`} 
          variant="outlined"
        />
        {selectedPages.size > 0 && (
          <Chip 
            label={`Est. Size: ${formatFileSize(getTotalSize())}`} 
            variant="outlined"
          />
        )}
      </Box>

      {/* Page List */}
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <List dense>
          {filteredPages.map((page, index) => (
            <React.Fragment key={page.url}>
              <ListItem
                button
                onClick={() => handlePageToggle(page.url)}
                sx={{ 
                  '&:hover': { backgroundColor: 'action.hover' },
                  backgroundColor: selectedPages.has(page.url) ? 'action.selected' : 'transparent'
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={selectedPages.has(page.url)}
                    onChange={() => handlePageToggle(page.url)}
                    color="primary"
                  />
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="body2" component="div">
                        <strong>{page.title || 'No title'}</strong>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {page.url}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                      <Chip 
                        label={`Depth ${page.depth}`} 
                        size="small" 
                        variant="outlined"
                      />
                      <Chip 
                        label={formatFileSize(page.size)} 
                        size="small" 
                        variant="outlined"
                      />
                      {page.status_code && (
                        <Chip 
                          label={`${page.status_code}`} 
                          size="small" 
                          color={page.status_code === 200 ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                />
              </ListItem>
              {index < filteredPages.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {filteredPages.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No pages match your current filters. Try adjusting your search terms or depth filter.
        </Alert>
      )}

      {/* Action Button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<PlayArrow />}
          onClick={handleStartScraping}
          disabled={selectedPages.size === 0}
          sx={{ minWidth: 200 }}
        >
          Start Scraping ({selectedPages.size} pages)
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
        <strong>Note:</strong> Scraping will respect rate limits and robots.txt. 
        Large selections may take considerable time to complete.
      </Typography>
    </Paper>
  );
};

export default PageSelector;
